import type { ReactNode } from 'react';
import { forwardRef } from 'react';

import { ImageUpload } from '@shared/core-ui-image-upload';
import { georgeStats, trackEvent2 } from '@shared/statistics';
import { deleteImage, uploadImage } from '@shared/image';
import { getEnv } from '@luna/env';
import type { Account } from '@shared/products-types';

import { gAPIAccountEntitySlice } from '../gAPIAccountEntitySlice';
import { invalidateProductImage } from '../papi-migration/hooks/useProductImage';
import { removeImageFromCache, saveImageToCache } from '../services/imageLoadingService';

type Props = {
    dropdownToggle?: ReactNode;
    productId: string;
    hasImage: boolean;
    productI18N?: string;
    productType?: string;
    imageChangeContext: 'accountDetails' | 'overview';
} & Parameters<typeof trackEvent2<'productCustomisationChanges', 'productImageChangeStarted'>>[2];

const IMAGE_PREFIX_LENGTH = 22;

export const ProductImageUpload = forwardRef<HTMLInputElement, Props>(
    (
        {
            dropdownToggle,
            productId,
            productI18N,
            hasImage,
            imageChangeContext,
            trigger,
            productType = '',
        },
        ref,
    ) => {
        const getExternalProductId = () => {
            const gapiAccount = gAPIAccountEntitySlice.selectById(productId) as Account | undefined;
            const externalId = gapiAccount?.productId;
            return externalId && externalId !== productId ? externalId : undefined;
        };

        const handleImageUpload = (imageUrl: string) =>
            uploadImage({
                uploadUrl: `${getEnv().georgeApiUrl}my/images`,
                imageDataUrl: imageUrl,
                data: new Map([['accountId', productId]]),
            }).then(() => {
                const imageBase64 = imageUrl.substr(IMAGE_PREFIX_LENGTH);
                const externalProductId = getExternalProductId();
                saveImageToCache(productId, imageBase64);
                invalidateProductImage(productId);

                if (externalProductId) {
                    saveImageToCache(externalProductId, imageBase64);
                    invalidateProductImage(externalProductId);
                }

                gAPIAccountEntitySlice.updateCache({
                    ...(gAPIAccountEntitySlice.selectById(productId) as Account),
                    hasImage: true,
                });
            });

        const handleImageCustomizationStart = () =>
            trackEvent2('productCustomisationChanges', 'productImageChangeStarted', {
                trigger,
            });

        const handleImageCustomizationSuccess = () =>
            trackEvent2('productCustomisationChanges', 'productCustomised', {
                productType,
                customisationChanges: ['image'],
            });

        const handleUploadedImageChange = () => {
            georgeStats.trackEvent(imageChangeContext, 'accountAvatarChanged', {
                productI18N,
            });
            handleImageCustomizationSuccess();
        };

        const handleImageRemove = async () => {
            handleImageCustomizationStart();
            return deleteImage(
                `${getEnv().georgeApiUrl}my/accounts/${productId}/images/image`,
            ).then(() => {
                const externalProductId = getExternalProductId();
                removeImageFromCache(productId);
                invalidateProductImage(productId);
                if (externalProductId) {
                    removeImageFromCache(externalProductId);
                    invalidateProductImage(externalProductId);
                }
                gAPIAccountEntitySlice.updateCache({
                    ...(gAPIAccountEntitySlice.selectById(productId) as Account),
                    hasImage: false,
                });
                handleImageCustomizationSuccess();
            });
        };

        return (
            <ImageUpload
                ref={ref}
                onUploadedImageChange={handleUploadedImageChange}
                onImageUpload={handleImageUpload}
                onImageRemove={handleImageRemove}
                disableRemoveImage={!hasImage}
                showResultAsNotification
                dropdownToggle={dropdownToggle}
                onChangeImageClick={handleImageCustomizationStart}
            />
        );
    },
);
